import { useEffect, useState } from 'react';
import { X, Trophy, Skull, Equal, Download } from 'lucide-react';
import { useDecks } from '@/hooks/useDecks';
import { useLogMatch, useMyMatches, lastUsedDeckId } from '@/hooks/useMatches';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

interface Props {
  open: boolean;
  onClose: () => void;
  /** When set, the match is associated with an event. */
  eventId?: string | null;
  /** Optional pre-filled opponent (when logging from event roster). */
  preOpponentId?: string | null;
  preOpponentLabel?: string;
}

export function MatchLogSheet({ open, onClose, eventId = null, preOpponentId = null, preOpponentLabel }: Props) {
  const { user } = useAuth();
  const { data: decks } = useDecks();
  const { data: matches } = useMyMatches();
  const log = useLogMatch();
  const [opponent, setOpponent] = useState(preOpponentLabel || '');
  const [myDeck, setMyDeck] = useState<string>('');
  const [result, setResult] = useState<'win' | 'loss' | 'draw'>('win');
  const [myScore, setMyScore] = useState('');
  const [oppScore, setOppScore] = useState('');
  const [notes, setNotes] = useState('');

  // Auto-link to most recently used deck when opening a fresh log sheet.
  useEffect(() => {
    if (!open || myDeck) return;
    const last = lastUsedDeckId(matches, user?.id);
    if (last && decks?.some(d => d.id === last)) setMyDeck(last);
    else if (decks && decks.length === 1) setMyDeck(decks[0].id);
  }, [open, matches, decks, user?.id, myDeck]);


  if (!open) return null;

  const handleSave = async () => {
    if (!opponent.trim() && !preOpponentId) {
      toast.error('Enter an opponent name');
      return;
    }
    try {
      await log.mutateAsync({
        eventId,
        opponentId: preOpponentId,
        opponentLabel: opponent.trim() || preOpponentLabel,
        myDeckId: myDeck || null,
        result,
        myScore: myScore ? parseInt(myScore, 10) : undefined,
        opponentScore: oppScore ? parseInt(oppScore, 10) : undefined,
        notes: notes.trim() || undefined,
      });
      toast.success('Match logged!');
      setOpponent(preOpponentLabel || '');
      setMyDeck(''); setMyScore(''); setOppScore(''); setNotes(''); setResult('win');
      onClose();
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-background/80 backdrop-blur-sm" onClick={onClose}>
      <div className="animate-slide-up w-full max-w-[430px] max-h-[85vh] overflow-y-auto rounded-t-2xl border border-border bg-card p-5" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-semibold text-foreground">Log Match</h3>
          <button onClick={onClose} className="text-muted-foreground"><X className="h-5 w-5" /></button>
        </div>

        {/* Opponent */}
        {preOpponentLabel ? (
          <div className="rounded-lg border border-border bg-secondary px-3 py-2.5 text-sm text-foreground font-body mb-3">
            vs <span className="font-semibold">{preOpponentLabel}</span>
          </div>
        ) : (
          <input
            type="text"
            value={opponent}
            onChange={e => setOpponent(e.target.value)}
            placeholder="Opponent name"
            className="w-full rounded-lg border border-border bg-secondary px-3 py-2.5 text-sm text-foreground font-body placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary mb-3"
          />
        )}

        {/* Result */}
        <div className="grid grid-cols-3 gap-1.5 mb-3">
          {([
            { v: 'win', label: 'Win', icon: Trophy, color: 'ink-emerald' },
            { v: 'loss', label: 'Loss', icon: Skull, color: 'ink-ruby' },
            { v: 'draw', label: 'Draw', icon: Equal, color: 'muted-foreground' },
          ] as const).map(opt => {
            const Icon = opt.icon;
            const active = result === opt.v;
            return (
              <button
                key={opt.v}
                onClick={() => setResult(opt.v)}
                className={`flex items-center justify-center gap-1.5 rounded-lg py-2.5 text-xs font-medium font-body transition-colors ${
                  active
                    ? opt.v === 'win' ? 'bg-ink-emerald/20 text-ink-emerald ring-1 ring-ink-emerald'
                    : opt.v === 'loss' ? 'bg-ink-ruby/20 text-ink-ruby ring-1 ring-ink-ruby'
                    : 'bg-secondary text-foreground ring-1 ring-primary'
                    : 'bg-secondary text-muted-foreground'
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {opt.label}
              </button>
            );
          })}
        </div>

        {/* Score (optional) */}
        <div className="grid grid-cols-2 gap-2 mb-3">
          <input
            type="number"
            inputMode="numeric"
            value={myScore}
            onChange={e => setMyScore(e.target.value)}
            placeholder="My lore"
            className="rounded-lg border border-border bg-secondary px-3 py-2.5 text-sm text-foreground font-body placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          />
          <input
            type="number"
            inputMode="numeric"
            value={oppScore}
            onChange={e => setOppScore(e.target.value)}
            placeholder="Opp lore"
            className="rounded-lg border border-border bg-secondary px-3 py-2.5 text-sm text-foreground font-body placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>

        {/* My deck */}
        {decks && decks.length > 0 && (
          <select
            value={myDeck}
            onChange={e => setMyDeck(e.target.value)}
            className="w-full rounded-lg border border-border bg-secondary px-3 py-2.5 text-sm text-foreground font-body focus:outline-none focus:ring-1 focus:ring-primary mb-3"
          >
            <option value="">Deck I played (optional)</option>
            {decks.map(d => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
        )}

        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          rows={2}
          placeholder="Notes (optional)"
          className="w-full rounded-lg border border-border bg-secondary px-3 py-2.5 text-sm text-foreground font-body placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary resize-none mb-3"
        />

        <button
          onClick={handleSave}
          disabled={log.isPending}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary py-3 text-sm font-medium text-primary-foreground font-body disabled:opacity-50"
        >
          <Download className="h-4 w-4" />
          {log.isPending ? 'Saving...' : 'Save Match'}
        </button>
      </div>
    </div>
  );
}
